import { useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import { useListAllCourtsQuery } from '@/store/courtsApi';
import type { CourtListItemDto } from '@/types';
import { includesIgnoringDiacritics } from '@/utils/normalize';
import {
  FilterRow,
  GroupSection,
  GroupHeading,
  ApelacjaRibbon,
  ChevronIcon,
  JumpBar,
  JumpButton,
} from './CourtsPage.styled';

type SortOption = 'sprawy-desc' | 'sprawy-asc' | 'name-asc' | 'name-desc' | 'city';

const TYPE_BADGE_VARIANT: Record<string, string> = {
  SadNajwyzszy: 'dark',
  NaczelnySadAdministracyjny: 'dark',
  WojewodzkiSadAdministracyjny: 'info',
  Apelacyjny: 'danger',
  Okregowy: 'warning',
  Rejonowy: 'primary',
  WojskowyOkregowy: 'success',
  WojskowyGarnizonowy: 'success',
  TrybunalKonstytucyjny: 'dark',
  TrybunalStanu: 'dark',
};

function groupKey(court: CourtListItemDto): string {
  if (court.apelacja) return `apelacja:${court.apelacja}`;
  if (court.type === 'WojewodzkiSadAdministracyjny' || court.type === 'NaczelnySadAdministracyjny')
    return 'sadyAdministracyjne';
  if (court.type === 'WojskowyOkregowy' || court.type === 'WojskowyGarnizonowy')
    return 'sadyWojskowe';
  if (court.type === 'TrybunalKonstytucyjny' || court.type === 'TrybunalStanu')
    return 'trybunaly';
  if (court.type === 'SadNajwyzszy')
    return 'sadNajwyzszy';
  return 'inne';
}

function translateGroupKey(key: string, t: (k: string, opts?: Record<string, string>) => string): string {
  if (key.startsWith('apelacja:')) {
    return t('courts.group.apelacja', { name: key.slice('apelacja:'.length) });
  }
  return t(`courts.group.${key}`);
}

function sortCourts(courts: CourtListItemDto[], sort: SortOption): CourtListItemDto[] {
  const sorted = [...courts];
  switch (sort) {
    case 'sprawy-desc': return sorted.sort((a, b) => b.sprawyCount - a.sprawyCount);
    case 'sprawy-asc': return sorted.sort((a, b) => a.sprawyCount - b.sprawyCount);
    case 'name-asc': return sorted.sort((a, b) => a.name.localeCompare(b.name, 'pl'));
    case 'name-desc': return sorted.sort((a, b) => b.name.localeCompare(a.name, 'pl'));
    case 'city': return sorted.sort((a, b) => a.cityName.localeCompare(b.cityName, 'pl'));
    default: return sorted;
  }
}

export default function CourtsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortOption>('sprawy-desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const { data: courts, isLoading } = useListAllCourtsQuery();
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const toggleGroup = useCallback((label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const scrollToGroup = useCallback((label: string) => {
    // Expand if collapsed
    setCollapsedGroups((prev) => {
      if (!prev.has(label)) return prev;
      const next = new Set(prev);
      next.delete(label);
      return next;
    });
    // Scroll after a tick so the DOM updates
    requestAnimationFrame(() => {
      groupRefs.current.get(label)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const filtered = useMemo(() => {
    if (!courts) return [];
    let result = courts;
    if (searchQuery) {
      result = result.filter((c) =>
        includesIgnoringDiacritics(c.name, searchQuery) ||
        includesIgnoringDiacritics(c.cityName, searchQuery) ||
        (c.address && includesIgnoringDiacritics(c.address, searchQuery))
      );
    }
    if (hideEmpty) {
      result = result.filter((c) => c.sprawyCount > 0);
    }
    return result;
  }, [courts, searchQuery, hideEmpty]);

  const sorted = useMemo(() => sortCourts(filtered, sort), [filtered, sort]);

  const grouped = useMemo(() => {
    const map = new Map<string, CourtListItemDto[]>();
    for (const court of sorted) {
      const label = groupKey(court);
      if (!map.has(label)) map.set(label, []);
      map.get(label)!.push(court);
    }
    return Array.from(map, ([label, courts]) => ({ label, courts }));
  }, [sorted]);

  const apelacjaLabels = useMemo(
    () => grouped.filter((g) => g.label.startsWith('apelacja:')).map((g) => g.label),
    [grouped],
  );

  const courtColumns: DataTableColumn<CourtListItemDto>[] = [
    { key: 'name', header: t('courts.table.name'), render: (c) => <strong>{c.name}</strong> },
    {
      key: 'type',
      header: t('courts.table.type'),
      render: (c) => (
        <Badge bg={TYPE_BADGE_VARIANT[c.type] ?? 'secondary'}>
          {t(`courtType.${c.type}`)}
        </Badge>
      ),
    },
    { key: 'city', header: t('courts.table.city'), render: (c) => c.cityName },
    { key: 'sprawy', header: t('courts.table.sprawy'), render: (c) => c.sprawyCount, align: 'right' },
  ];

  return (
    <PageContent>
      <PageHeader title={t('courts.title')} subtitle={t('courts.subtitle')} />

      <FilterRow>
        <Form.Control
          type="text"
          placeholder={t('courts.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Form.Select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortOption)}
        >
          <option value="sprawy-desc">{t('courts.sort.sprawyDesc')}</option>
          <option value="sprawy-asc">{t('courts.sort.sprawyAsc')}</option>
          <option value="name-asc">{t('courts.sort.nameAsc')}</option>
          <option value="name-desc">{t('courts.sort.nameDesc')}</option>
          <option value="city">{t('courts.sort.city')}</option>
        </Form.Select>
        <Form.Check
          type="switch"
          label={t('courts.hideWithoutCases')}
          checked={hideEmpty}
          onChange={(e) => setHideEmpty(e.target.checked)}
        />
      </FilterRow>

      {apelacjaLabels.length > 0 && (
        <JumpBar>
          {apelacjaLabels.map((label) => (
            <JumpButton key={label} onClick={() => scrollToGroup(label)}>
              {label.slice('apelacja:'.length)}
            </JumpButton>
          ))}
        </JumpBar>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : !courts?.length ? (
        <div className="text-center py-5 text-muted">
          <h5>{t('courts.empty')}</h5>
        </div>
      ) : (
        <>
          {grouped.map((group) => {
            const isApelacja = group.label.startsWith('apelacja:');
            const isCollapsed = collapsedGroups.has(group.label);

            return (
              <GroupSection key={group.label} ref={(el) => { if (el) groupRefs.current.set(group.label, el); }}>
                {isApelacja ? (
                  <ApelacjaRibbon $collapsed={isCollapsed} onClick={() => toggleGroup(group.label)}>
                    <h2>{`${translateGroupKey(group.label, t)} (${group.courts.length})`}</h2>
                    <ChevronIcon $collapsed={isCollapsed}>{'\u25BC'}</ChevronIcon>
                  </ApelacjaRibbon>
                ) : (
                  <GroupHeading>{translateGroupKey(group.label, t)}</GroupHeading>
                )}
                {!isCollapsed && (
                  <DataTable<CourtListItemDto>
                    ariaLabel={translateGroupKey(group.label, t)}
                    rowKey={(c) => String(c.id)}
                    onRowClick={(c) => navigate(`/courts/${c.id}`)}
                    columns={courtColumns}
                    rows={group.courts}
                  />
                )}
              </GroupSection>
            );
          })}
        </>
      )}
    </PageContent>
  );
}
